import React, { useState } from 'react';

import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';

import { IconButton } from '../../atoms';
import { X as XIcon } from '../../icons';

const useStyles = makeStyles((theme: Theme) => ({
  childrenContainer: {
    background: theme.palette.grey[200],
  },
  childrenContent: {
    marginTop: -theme.spacing(2.5),
    padding: theme.spacing(0, 3, 1.5, 2),
  },
  childrenContentNoCloseBtn: {
    marginTop: 0,
    padding: theme.spacing(1.5, 2, 1.5),
  },
  closeButton: {
    background: theme.palette.grey[200],
    display: 'flex',
    margin: '0px 0px auto auto',
    padding: theme.spacing(1),

    '&.MuiIconButton-colorPrimary:hover': {
      backgroundColor: 'transparent',
      '& svg': {
        color: theme.palette.common.black,
      },
    },
  },
  beforeAnimation: {
    height: 0,
  },
  animationStarted: {
    height: 'auto',
  },
  enterAnimation: {
    animation: '$show 200ms ease-out',
    animationDelay: '300ms',
  },
  '@keyframes show': {
    '0%': {
      marginTop: '-200px',
    },
    '100%': {
      marginTop: theme.spacing(1.5),
    },
  },
  bounceAnimation: {
    animation: '$bounce 100ms ease-in',
  },
  '@keyframes bounce': {
    '0%': {
      marginTop: theme.spacing(1.5),
    },
    '100%': {
      marginTop: '0px',
    },
  },
  pointer: {
    borderColor: `transparent transparent ${theme.palette.grey[200]} transparent`,
    borderStyle: 'solid',
    borderWidth: '16px 16px 16px 16px',
    height: '0px',
    width: '0px',
  },
  pointerRight: {
    margin: `-${theme.spacing(2)} 0px auto auto`,
    marginRight: theme.spacing(2.25),
  },
  pointerLeft: {
    margin: `-${theme.spacing(2)} auto auto 0px`,
    marginLeft: theme.spacing(0.25),
  },
  pointerCenter: {
    margin: `-${theme.spacing(2)} auto auto auto`,
  },
  popout: {
    minHeight: 0,
    overflow: 'hidden',
    margin: theme.spacing(0, -2, 2),
  },

  [theme.breakpoints.up('sm')]: {
    popout: {
      margin: theme.spacing(0, 0, 2),
    },
    childrenContainer: {
      margin: theme.spacing(0, 0, 2),
    },
    pointerRight: {
      marginRight: theme.spacing(4),
    },
    pointerLeft: {
      marginLeft: theme.spacing(4),
    },
  },
}));

export type PopoutProps = {
  children: JSX.Element;
  className?: string;
  closeIconAriaLabel?: string;
  onClosePopout?: () => void;
  pointerVariant?: 'left' | 'center' | 'right' | 'none';
  popoutName?: string;
  showCloseButton?: boolean;
  showPopout: boolean;
};

export function Popout(props: PopoutProps): JSX.Element {
  const {
    children,
    className = '',
    closeIconAriaLabel = '',
    onClosePopout = (): void => {},
    pointerVariant = 'right',
    popoutName = '',
    showCloseButton = true,
    showPopout,
  } = props;
  const classes = useStyles();
  const [enterAnimationStarted, setEnterAnimationStarted] =
    useState<boolean>(false);
  const [bounceAnimationStarted, setBounceAnimationStarted] =
    useState<boolean>(false);

  const popoutStyles = clsx(
    'fd-popout',
    classes.popout,
    popoutName && `fd-popout__${popoutName.toLowerCase()}`,
    className !== '' && className,
  );
  const pointerStyles = clsx(
    `fd-popout__pointer_${pointerVariant}`,
    classes.pointer,
    pointerVariant === 'right' && classes.pointerRight,
    pointerVariant === 'left' && classes.pointerLeft,
    pointerVariant === 'center' && classes.pointerCenter,
  );

  const contentContainerStyles = clsx(
    classes.enterAnimation,
    enterAnimationStarted ? classes.animationStarted : classes.beforeAnimation,
    bounceAnimationStarted && classes.bounceAnimation,
  );
  const childrenStyles = clsx(
    'fd-popout__children',
    classes.childrenContent,
    !showCloseButton && classes.childrenContentNoCloseBtn,
  );

  if (!showPopout) {
    return null;
  }

  return (
    <div className={popoutStyles}>
      <div
        className={contentContainerStyles}
        onAnimationStart={(): void => {
          setEnterAnimationStarted(true);
        }}
        onAnimationEnd={(): void => {
          setBounceAnimationStarted(true);
        }}
      >
        {pointerVariant !== 'none' && <div className={pointerStyles} />}
        <div className={classes.childrenContainer}>
          {showCloseButton && (
            <IconButton
              aria-label={closeIconAriaLabel}
              className={classes.closeButton}
              disableRipple
              onClick={onClosePopout}
            >
              <XIcon size="xs" />
            </IconButton>
          )}

          <div className={childrenStyles}>{children}</div>
        </div>
      </div>
    </div>
  );
}
